import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { ROUTES } from 'routes/constants';
import { processChallenges, hasStandardChallenges as hasStd, hasCustomChallenges as hasCustom, challengeMap } from './helpers';

interface ChallengePriority {
  priority: number;
  description: string;
}

interface ProductionDifficulties {
  [key: string]: number | ChallengePriority[];
  others: ChallengePriority[];
}

export const DificuldadesProducaoVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [dados, setDados] = useState<ProductionDifficulties | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) {
        setError('UUID não encontrado');
        setLoading(false);
        return;
      }

      try {
        // Tenta restaurar do localStorage (per-collection -> global)
        try {
          const storageKeys: string[] = [];
          if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
          storageKeys.push('cuffa-storage');

          for (const storageKey of storageKeys) {
            try {
              const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
              if (!raw) continue;
              const parsed = JSON.parse(raw);
              const saved = parsed?.state?.dadosParciais?.dificuldadesProducao ?? parsed?.state?.dadosParciais?.dificuldades_producao;
              if (!saved) continue;

              // Se já estiver na forma da API (tem 'others' com objetos), usa diretamente
              if (saved.others && Array.isArray(saved.others)) {
                setDados(saved as ProductionDifficulties);
                setLoading(false);
                return;
              }

              // Caso seja a forma salva pelo front (desafiosSelecionados, prioridades, outrosDesafios)
              const prioridadesArr: any[] = saved.prioridades ?? saved.priorities ?? [];
              const outrosArr: string[] = saved.outrosDesafios ?? saved.others ?? [];

              // Inverte o mapa para encontrar a chave de API a partir do texto exibido
              const displayToApi: Record<string, string> = Object.fromEntries(
                Object.entries(challengeMap).map(([apiKey, display]) => [display, apiKey])
              );

              const produced: any = {};
              // Inicializa chaves padrão
              Object.keys(challengeMap).forEach(k => { produced[k] = 0; });

              const othersResult: { description: string; priority: number }[] = [];

              prioridadesArr.forEach((p: any) => {
                const val = p?.value ?? p?.description ?? '';
                const priority = Number(p?.priority ?? p?.priority ?? 0) || 0;
                const apiKey = displayToApi[val];
                if (apiKey) {
                  produced[apiKey] = priority;
                } else if (val) {
                  othersResult.push({ description: val, priority });
                }
              });

              // Também inclua quaisquer outros custom que não tenham prioridade listada
              (outrosArr || []).forEach((o: any) => {
                if (!othersResult.some(r => r.description === o)) {
                  othersResult.push({ description: o, priority: 0 });
                }
              });

              produced.others = othersResult;

              setDados(produced as ProductionDifficulties);
              setLoading(false);
              return;
            } catch (e) {
              console.warn(`Falha ao parsear ${storageKey} ao restaurar dificuldadesProducao:`, e);
              continue;
            }
          }
        } catch (e) {
          console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar dificuldadesProducao', e);
        }

        // Se não encontrou no storage, chama API
        const response = await CuffaAPI.getDataByID(uuid);
        const apiData = response?.dados?.production_difficulties;

        if (!apiData) {
          setError('Dados de dificuldades de produção não encontrados');
          setLoading(false);
          return;
        }

        setDados(apiData);

      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setError('Erro ao carregar os dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid]);

  // lógica de processamento extraída para helpers

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Dificuldades à Produção" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (error) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Dificuldades à Produção" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Alert severity="error">{error}</Alert>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Dificuldades à Produção" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Alert severity="info">Nenhum dado encontrado</Alert>
        </Box>
      </MenuProvider>
    );
  }

  const desafiosOrdenados = processChallenges(dados);
  const hasStandardChallenges = hasStd(dados);
  const hasCustomChallenges = hasCustom(dados);

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização das Dificuldades à Produção" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Dificuldades à Produção da Unidade Familiar" width="100%">
            {/* Seção: Desafios ordenados por prioridade */}
            {(hasStandardChallenges || hasCustomChallenges) ? (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Desafios por Ordem de Prioridade
                </Typography>

                {desafiosOrdenados.length > 0 ? (
                  <List>
                    {desafiosOrdenados.map((desafio, index) => (
                      <div key={`${desafio.value}-${index}`}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={2}>
                                <Chip
                                  label={`${desafio.priority}º`}
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                />
                                <Typography>
                                  {desafio.value}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < desafiosOrdenados.length - 1 && <Divider />}
                      </div>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">Nenhum desafio registrado</Alert>
                )}
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Alert severity="info">Nenhuma dificuldade de produção registrada</Alert>
              </Paper>
            )}

            {/* Seção: Desafios personalizados adicionados */}
            {hasCustomChallenges && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Desafios Personalizados Adicionados
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {dados.others.map((outro, index) => (
                    <Chip
                      key={`outro-${index}`}
                      label={outro.description}
                      color="secondary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.PARTICIPACAO_FEMININA_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(ROUTES.CUFFA.replace(':uuid', uuid ?? ''))}
              >
                Concluir
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default DificuldadesProducaoVisualizar;
